import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Handshake,
  Truck,
  Building2,
  TrendingDown,
  Plus,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { PartnerFormDialog } from "@/components/PartnerFormDialog";
import { SubscriptionFormDialog } from "@/components/SubscriptionFormDialog";

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });
const formatFt = (n) => (n == null ? "—" : `${huFmt.format(n)} Ft`);
const PAYMENT_TERMS_LABEL = {
  on_delivery: "Átvételkor",
  transfer_8: "8 nap átutalás",
  transfer_10: "10 nap átutalás",
  transfer_15: "15 nap átutalás",
};

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

export default function PartnerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [prices, setPrices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: p } = await api.get(`/partners/${id}`);
      setPartner(p);
      if (p.partner_type === "supplier") {
        const { data: pr } = await api.get(`/partners/${id}/prices`);
        setPrices(pr);
      } else {
        setPrices([]);
      }
      const { data: subs } = await api.get(`/subscriptions?partner_id=${id}`);
      setSubscriptions(subs);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onDelete = async () => {
    if (!window.confirm(`Törlöd a "${partner.name}" partnert?`)) return;
    try {
      await api.delete(`/partners/${id}`);
      toast.success("Partner törölve.");
      navigate("/partners");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  if (loading || !partner) {
    return (
      <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  const isSupplier = partner.partner_type === "supplier";

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <div>
        <button onClick={() => navigate("/partners")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Partnerek
        </button>
      </div>

      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            {isSupplier ? <Truck className="h-3.5 w-3.5" strokeWidth={2} /> : <Handshake className="h-3.5 w-3.5" strokeWidth={2} />}
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
              {isSupplier ? "Beszállító" : "Partner"}
            </span>
          </div>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">{partner.name}</h1>
          {partner.short_name && <p className="mt-0.5 text-sm text-muted-foreground">{partner.short_name}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-partner-button">
            <Pencil className="h-3.5 w-3.5" /> Szerkesztés
          </Button>
          <Button variant="outline" size="sm" onClick={onDelete}
            className="text-destructive hover:text-destructive" data-testid="delete-partner-button">
            <Trash2 className="h-3.5 w-3.5" /> Törlés
          </Button>
        </div>
      </header>

      <Tabs defaultValue="data">
        <TabsList className="bg-muted/40">
          <TabsTrigger value="data" data-testid="partner-tab-data">Adatok</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="partner-tab-contacts">
            Kapcsolattartók ({(partner.contacts || []).length})
          </TabsTrigger>
          {isSupplier && (
            <TabsTrigger value="prices" data-testid="partner-tab-prices">
              Beszállítói árak ({prices.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="subscriptions" data-testid="partner-tab-subscriptions">
            Előfizetések ({subscriptions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="mt-5 space-y-6">
          <Card title="Cég adatai">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <Row label="Rövid név" value={partner.short_name} />
              <Row label="Típus" value={isSupplier ? "Beszállító" : "Partner"} />
              <Row label="Adószám" value={partner.tax_id} mono />
              <Row label="Cég székhelye" value={partner.hq_address} />
              <Row
                label="Rendelési e-mail cím"
                value={partner.order_email}
                wide
              />
            </dl>
            {isSupplier && !partner.order_email && (
              <p
                className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                data-testid="missing-order-email-hint"
              >
                Nincs rögzítve rendelési e-mail cím — emiatt ehhez a beszállítóhoz
                a rendszer nem enged rendelést indítani (csak a beszállító saját
                weboldalán).
              </p>
            )}
          </Card>

          <Card title="Banki adatok és fizetés">
            <DataGrid>
              <Row label="Bank neve" value={partner.bank_name} />
              <Row label="Számlaszám" value={partner.bank_account_number} mono />
              <Row label="Fizetési mód" value={PAYMENT_TERMS_LABEL[partner.payment_terms]} />
            </DataGrid>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="mt-5">
          <Card title="Kapcsolattartók">
            {(partner.contacts || []).length === 0 ? (
              <p className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-xs text-muted-foreground">
                Még nincs rögzített kapcsolattartó.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {partner.contacts.map((c, idx) => (
                  <li key={idx} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="font-medium">{c.name}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:underline">
                          <Phone className="h-3 w-3" /> {c.phone}
                        </a>
                      )}
                      {c.email && (
                        <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:underline">
                          <Mail className="h-3 w-3" /> {c.email}
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        {isSupplier && (
          <TabsContent value="prices" className="mt-5">
            <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
              <header className="border-b border-border px-5 py-4">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Beszállítói árak
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {prices.length === 0
                    ? "Még nincs ár rögzítve. Termék oldalán tudsz felvenni új árat."
                    : `${prices.length} termék ára van rögzítve.`}
                </p>
              </header>
              {prices.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                  <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
                    <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm font-medium">Nincs rögzített ár</p>
                  <Link to="/supplier-products" className="text-xs text-blue-600 hover:underline dark:text-blue-400">
                    Ugrás a termékekhez →
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                        <Th>Termék</Th>
                        <Th>Egység</Th>
                        <Th className="text-right">Nettó ár</Th>
                        <Th className="text-right">ÁFA</Th>
                        <Th className="text-right">Bruttó ár</Th>
                        <Th>Megjegyzés</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {prices.map((p) => (
                        <tr key={p._id} data-testid={`partner-price-row-${p._id}`}
                          className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]">
                          <Td>
                            <Link to={`/supplier-products/${p.product_id}`} className="inline-flex items-center gap-1.5 font-medium hover:underline">
                              <TrendingDown className="h-3.5 w-3.5 text-muted-foreground" /> {p.product_name}
                            </Link>
                          </Td>
                          <Td className="text-muted-foreground">{p.product_unit || "—"}</Td>
                          <Td className="text-right tabular-nums font-medium">{formatFt(p.net_price)}</Td>
                          <Td className="text-right tabular-nums text-muted-foreground">{p.vat_rate}%</Td>
                          <Td className="text-right tabular-nums">{formatFt(p.gross_price)}</Td>
                          <Td className="text-muted-foreground">{p.notes || "—"}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </TabsContent>
        )}

        <TabsContent value="subscriptions" className="mt-5">
          <PartnerSubscriptionsList
            partnerId={id}
            subscriptions={subscriptions}
            onChanged={load}
          />
        </TabsContent>
      </Tabs>

      <PartnerFormDialog
        open={editing}
        onOpenChange={setEditing}
        partner={partner}
        onSaved={(updated) => setPartner(updated)}
      />
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
      <header className="border-b border-border px-5 py-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </p>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function DataGrid({ children }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </dl>
  );
}

function Row({ label, value, mono, wide }) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </dt>
      <dd className={`mt-0.5 text-sm ${mono ? "font-mono" : ""} ${!value && "text-muted-foreground/60"}`}>
        {value || "—"}
      </dd>
    </div>
  );
}

const FREQ_LABEL = { monthly: "Havi", quarterly: "Negyedéves", half_year: "Féléves", yearly: "Éves" };
const CONTRACT_LABEL = { fixed_term: "Határozott", open_ended: "Határozatlan" };
const subHuFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });
const fmtMoney = (p, c) => (p == null ? "—" : `${subHuFmt.format(p)} ${c || "HUF"}`);
const fmtDate = (s) => (s ? s : "—");

function PartnerSubscriptionsList({ partnerId, subscriptions, onChanged }) {
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    api.get(`/partners/${partnerId}`).then(({ data }) => setPartner(data)).catch(() => {});
  }, [partnerId]);

  const onDelete = async (s) => {
    if (!window.confirm(`Törlöd a(z) "${s.name}" előfizetést?`)) return;
    try {
      await api.delete(`/subscriptions/${s._id}`);
      toast.success("Előfizetés törölve.");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
      <header className="flex flex-col gap-1 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Előfizetések</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {subscriptions.length === 0 ? "Még nincs előfizetés rögzítve." : `${subscriptions.length} előfizetés`}
          </p>
        </div>
        <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="partner-new-subscription">
          <Plus className="h-4 w-4" /> Új előfizetés
        </Button>
      </header>
      {subscriptions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
            <CreditCard className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium">Nincs rögzített előfizetés</p>
          <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
            <Plus className="h-3.5 w-3.5" /> Új előfizetés
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Név</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em]">Ár</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Gyakoriság</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Szerződés</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Lejárat</th>
                <th className="px-4 py-3 pr-4 text-right text-[11px] font-medium uppercase tracking-[0.12em]">Művelet</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((s) => (
                <tr key={s._id} className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]">
                  <td className="px-4 py-3"><span className="font-medium">{s.name}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtMoney(s.price, s.currency)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{FREQ_LABEL[s.billing_frequency] || s.billing_frequency}</td>
                  <td className="px-4 py-3">{CONTRACT_LABEL[s.contract_type] || s.contract_type}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">{fmtDate(s.expiry_date)}</td>
                  <td className="px-4 py-3 pr-4 text-right">
                    <div className="ml-auto flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(s)} aria-label="Szerkesztés"
                        className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onDelete(s)} aria-label="Törlés"
                        className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubscriptionFormDialog
        open={openCreate || !!editing}
        onOpenChange={(v) => { if (!v) { setOpenCreate(false); setEditing(null); } }}
        subscription={editing}
        partners={partner ? [partner] : []}
        defaultPartnerId={partnerId}
        onSaved={() => onChanged()}
      />
    </section>
  );
}

